<template>
  <div>
    <p class="has-text-grey mb-1">{{ $t("mp:registration.welcome.title") }}</p>

    <p class="is-size-3 has-text-weight-bold">
      {{ $t("mp:registration.title") }}
    </p>

    <form @submit.prevent="doRegistration">
      <validation-observer ref="observer">
        <BInputWithValidation
          :label="$t('mp:registration.input.contact_last_name.label')"
          rules="required"
          label-position="on-border"
          v-model="registration.lastName"
        />

        <BInputWithValidation
          :label="$t('mp:registration.input.contact_first_name.label')"
          rules="required"
          label-position="on-border"
          v-model="registration.firstName"
        />

        <BInputWithValidation
          :label="$t('mp:registration.input.merchant_name.label')"
          rules="required"
          label-position="on-border"
          v-model="registration.merchantFullName"
        />

        <BInputWithValidation
          :label="$t('mp:registration.input.vat_number.label')"
          rules="required"
          label-position="on-border"
          v-model="registration.merchantVatNumber"
        />

        <BInputWithValidation
          :label="$t('mp:registration.input.email.label')"
          rules="required|email"
          label-position="on-border"
          v-model="registration.email"
        />

        <CountrySelect
          v-model="registration.countryId"
          :label="$t('mp:profile.personal_data.language.label')"
        />

        <PhoneInput
          :label="$t('mp:registration.input.phone.label')"
          v-model="registration.phone"
        />

        <b-field
          :label="$t('mp:registration.input.password.label')"
          rules="required"
          label-position="on-border"
        >
          <PasswordCheckerInput
            v-model="registration.password"
            @validated="passwordValid = $event"
          />
        </b-field>

        <StatementsAcceptance
          :statements="statements"
          layout-type="MERCHANT_REGISTRATION"
          read-required-validation-message="mp:registration.message.validation.statement_read_required"
          ref="statementAcceptances"
          :is-public="true"
        />
      </validation-observer>

      <b-button
        type="is-blue"
        native-type="submit"
        :loading="loading"
        expanded
        >{{ $t("mp:registration.button.register") }}</b-button
      >
    </form>

    <div class="mt-5">
      <span>{{ $t("mp:registration.login.title") }}</span>
      <router-link class="ml-2" to="/login">{{
        $t("mp:registration.login.button")
      }}</router-link>
    </div>
  </div>
</template>

<script>
import { generateNonce } from "../utils/util";
import i18n from "../plugins/i18n";
import PhoneInput from "../components/input/PhoneInput";
import PasswordCheckerInput from "../components/input/PasswordCheckerInput";
import StatementsAcceptance from "../components/statement/StatementsAcceptance";
import BInputWithValidation from "../components/input/BInputWithValidation";
import { LOGIN_STATUS } from "../utils/const";
import { mapActions, mapGetters, mapMutations } from "vuex";
import CountrySelect from "../components/input/CountrySelect.vue";

export default {
  name: "Registration",
  title: "mp:page.registration.title",
  components: {
    StatementsAcceptance,
    PhoneInput,
    BInputWithValidation,
    PasswordCheckerInput,
    CountrySelect,
  },
  props: {
    initialConfigPublicId: {
      type: String,
      required: false,
    },
  },
  async created() {
    await this.$store.dispatch("statements/fetchRegistrationStatements");
    this.statements = this.$store.getters["statements/registrationStatements"];
    this.statements.forEach((rc) => {
      if (rc.promptDocument === "null") {
        rc.promptDocument = null;
      }
    });
    this.registration.initialConfigPublicId = this.initialConfigPublicId;
    await this.fetchAllCountries();
    this.setCountryByLanguageIso(this.routerLocale);
    this.registration.countryId = this.country.id;
  },
  data() {
    return {
      loading: false,
      passwordValid: false,
      statements: [],
      registration: {
        nonce: generateNonce(),
        lastName: "",
        firstName: "",
        merchantFullName: "",
        merchantVatNumber: "",
        email: "",
        countryId: null,
        phone: "",
        password: "",
        initialConfigPublicId: "",
      },
    };
  },
  computed: {
    ...mapGetters({
      countries: "i18n/countries",
      country: "i18n/country",
    }),
    routerLocale() {
      return this.$route.params.locale;
    },
    locale() {
      return this.country?.languageIso;
    },
  },
  methods: {
    ...mapActions({
      fetchAllCountries: "i18n/fetchAllCountries",
    }),
    ...mapMutations({
      setCountryByLanguageIso: "i18n/setCountryByLanguageIso",
    }),
    async doRegistration() {
      try {
        let statementAcceptances = this.$refs.statementAcceptances;
        let formValid = await this.$refs.observer.validate();
        let statementsValid = await statementAcceptances.validate();

        if (!(formValid && this.passwordValid && statementsValid)) {
          return;
        }

        this.loading = true;

        let request = this.registration;
        request.acceptedStatements = this.getAcceptedStatements();

        let question = await this.$store.dispatch("session/register", request);
        await this.$store.dispatch("session/saveSecondFactor", {
          id: question.id,
          nonce: request.nonce,
          status: LOGIN_STATUS.EMAIL_NOT_CONFIRMED,
        });
        await this.$router.push("/second-factor");
      } catch (ex) {
        console.warn(ex);
        if (ex.response.status === 409) {
          this.$buefy.toast.open({
            message: i18n.t("mp:registration.error.already_exists"),
            type: "is-danger",
          });
        }
      }
      this.loading = false;
    },
    getAcceptedStatements() {
      let statements = [];

      let statementAcceptances = this.$refs.statementAcceptances;
      for (let rc in this.statements) {
        let statement = this.statements[rc];
        if (statementAcceptances.statementValueBucket[statement.promptId]) {
          statements.push({
            statementId: statement.promptId,
            statementIdentifier: statement.promptIdentifier,
            documentId: statement.promptDocument,
            link: statement.link,
            statementLabel: statement.promptLabel,
            isRequired: statement.isRequired,
          });
        }
      }
      return statements;
    },
  },
  watch: {
    locale(newValue) {
      if (this.routerLocale && this.routerLocale !== this.locale) {
        this.$router.push({
          name: "LangRegistration",
          params: {
            locale: newValue,
          },
        });
      }
    },
  },
};
</script>

<style scoped lang="scss"></style>
